import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { updatePassword } from 'aws-amplify/auth';
import { CircleCheckIcon, CopyIcon, CheckIcon } from '../../assets/icons';
import * as userActions from '../../store/actions/user';
import * as apiKeyActions from '../../store/actions/apiKey';
import * as configActions from '../../store/actions/config';
import Layout from '../../components/Layout';
import { UserType } from '../../enums';
import ErrorToast from '../../components/ErrorToast';
import SuccessToast from '../../components/SuccessToast';
import {
  getOnRampAccountByCurrency,
  getAvailableOnRampCurrencies,
  getOnRampRailsForCurrency,
  getPreferredRailType,
  getPaymentMethodLabel,
  getOnRampAccountFields,
} from '../../utils/onRampHelpers';
import {
  accountCardClass,
  OutlineButton,
  ProfileInfoSection,
  SecurityActionRow,
  SettingsPanel,
  ToggleSwitch,
} from './AccountSections';
import AccountRampSettings from './AccountRampSettings';
import {
  buildProfileAddressRows,
  buildProfileContactRows,
  getCompanyName,
  getInitials,
  getIsCompanyUser,
  getProfileDisplayName,
  buildCompanyUpdatePayloadFromProfileForm,
  getPasswordChangeErrorMessage,
  getProfileFormFromUser,
  isValidProfileWebsite,
} from '../../utils/accountSettingsHelpers';
import {
  DECIMAL_LAYOUT,
  getDecimalLayout,
  buildDecimalLayoutPatch,
  formatNumberWithDecimalLayout,
} from '../../utils/userPreferencesHelpers';

const VALID_TABS = ['profile', 'security', 'banking', 'rampSettings', 'webhooks'];

// Temporarily hide API keys + webhooks 
const SHOW_ACCOUNT_RAMP_SETTINGS = false;

const inputClassName =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm';

const SECTION_SCROLL_IDS = {
  profile: 'account-section-profile',
  security: 'account-section-security',
  banking: 'account-section-banking',
  rampSettings: 'account-section-ramp',
  webhooks: 'account-section-ramp',
};

function Account() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

 const {
  user,
  updatingUser,
  updateUserError,
  bankAccount,
  onRampAccounts,
  gettingUser,
  preferences,
  upsertingUserPreferences,
} = useSelector((state) => state.user);
const { supportedOnRampCurrencies } = useSelector((state) => state.config);
const { apiKeys, loading: apiKeysLoading, error: apiKeysError } = useSelector((state) => state.apiKey);

  const [selectedOnRampCurrency, setSelectedOnRampCurrency] = useState('EUR');
  const [selectedOnRampRailType, setSelectedOnRampRailType] = useState(null);

  const isCompany = user?.userType === UserType.Company;
  const isCompanyUser = getIsCompanyUser(user);
  const decimalLayout = getDecimalLayout(preferences);

  const [profileEditOpen, setProfileEditOpen] = useState(false);
  const [passwordEditOpen, setPasswordEditOpen] = useState(false);
  const [profileForm, setProfileForm] = useState(() => getProfileFormFromUser(user));
  const [securityForm, setSecurityForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [newApiKeyName, setNewApiKeyName] = useState('');
  const [toastError, setToastError] = useState(null);
  const [toastSuccess, setToastSuccess] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState({});
  const [justUpdatedProfile, setJustUpdatedProfile] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [webhookForm, setWebhookForm] = useState({
    endpointUrl: '',
    events: {
      crossBorderQuoteNew: false,
      crossBorderExecuteNew: false,
      crossBorderExecuteUpdate: false,
      crossBorderExecuteComplete: false,
      recipientNew: false,
      bankAccountNew: false,
      payoutUpdate: false,
      payoutPartnerFee: false,
      payinNew: false,
      payinComplete: false,
      recipientUpdate: false,
      payoutNew: false,
      payoutComplete: false,
      blockchainWalletNew: false,
      payinUpdate: false,
      payinPartnerFee: false,
    },
  });

  const isValidTab = useCallback((tab) => VALID_TABS.includes(tab), []);

  useEffect(() => {
    dispatch(userActions.getBankAccount());
    dispatch(configActions.getConfig());
  }, [dispatch]);

  useEffect(() => {
    const profileMissing = !user?.email && !user?.company?.name && !user?.userDetails?.firstName;
    if (profileMissing && !gettingUser) {
      dispatch(userActions.getUser());
    }
  }, [dispatch, user, gettingUser]);

  useEffect(() => {
    if (isCompany) {
      dispatch(apiKeyActions.getApiKeys());
    }
  }, [isCompany, dispatch]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && !isValidTab(tab)) {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, isValidTab]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (!tab || !isValidTab(tab)) return;

    if (tab === 'security') setPasswordEditOpen(true);

    const scrollTarget = SECTION_SCROLL_IDS[tab];
    if (scrollTarget) {
      requestAnimationFrame(() => {
        document.getElementById(scrollTarget)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [searchParams, isValidTab]);

  useEffect(() => {
    if (user && !profileEditOpen) {
      setProfileForm(getProfileFormFromUser(user));
    }
  }, [user, profileEditOpen]);

  useEffect(() => {
    if (justUpdatedProfile && !updatingUser && !updateUserError) {
      setToastSuccess('Profile updated successfully.');
      setJustUpdatedProfile(false);
      setProfileEditOpen(false);
    }
    if (justUpdatedProfile && !updatingUser && updateUserError) {
      setToastError(
        updateUserError.detail || updateUserError.message || 'Failed to update profile. Please try again.',
      );
      setJustUpdatedProfile(false);
    }
  }, [justUpdatedProfile, updatingUser, updateUserError]);

  const handleProfileSubmit = (e) => {
    e.preventDefault();

    if (!isCompanyUser) {
      setToastError('Profile updates are only available for business accounts.');
      return;
    }

    if (profileForm.website?.trim() && !isValidProfileWebsite(profileForm.website)) {
      setToastError('Enter a valid website URL.');
      return;
    }

    const payload = buildCompanyUpdatePayloadFromProfileForm(profileForm, user);
    if (!payload) {
      setToastError('Unable to save profile. A registered business address is required.');
      return;
    }

    setJustUpdatedProfile(true);
    dispatch(userActions.updateCompany(payload));
  };

  const handleSecuritySubmit = async (e) => {
    e.preventDefault();

    if (!securityForm.currentPassword?.trim()) {
      setToastError('Enter your current password.');
      return;
    }
    if (!securityForm.newPassword?.trim()) {
      setToastError('Enter a new password.');
      return;
    }
    if (securityForm.newPassword !== securityForm.confirmPassword) {
      setToastError('New passwords do not match.');
      return;
    }

    setChangingPassword(true);
    setToastError(null);

    try {
      await updatePassword({
        oldPassword: securityForm.currentPassword,
        newPassword: securityForm.newPassword,
      });
      setSecurityForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordEditOpen(false);
      setToastSuccess('Password updated successfully.');
    } catch (error) {
      setToastError(getPasswordChangeErrorMessage(error));
    } finally {
      setChangingPassword(false);
    }
  };

  const handleProfileChange = (field, value) => {
    setProfileForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCancelProfileEdit = () => {
    setProfileForm(getProfileFormFromUser(user));
    setProfileEditOpen(false);
  };

  const handleSecurityChange = (field, value) => {
    setSecurityForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleApiKeyNameChange = (value) => {
    setNewApiKeyName(value);
  };

  const handleCreateApiKey = async (e) => {
    e.preventDefault();
    if (newApiKeyName.trim()) {
      const trimmedName = newApiKeyName.trim();
      const isDuplicate = apiKeys?.some((existingKey) => existingKey.name.toLowerCase() === trimmedName.toLowerCase());

      if (isDuplicate) {
        setToastError('An API key with this name already exists. Please choose a different name.');
        return;
      }

      try {
        await dispatch(apiKeyActions.addApiKey(trimmedName));
        setTimeout(() => {
          dispatch(apiKeyActions.getApiKeys());
        }, 500);
        setNewApiKeyName('');
        setToastSuccess('API key created successfully!');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error creating API key:', error);
        setToastError('Failed to create API key. Please try again.');
      }
    }
  };

  const handleDeleteApiKey = async (apiKey) => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      try {
        await dispatch(apiKeyActions.deleteApiKey(apiKey));
        setTimeout(() => {
          dispatch(apiKeyActions.getApiKeys());
        }, 500);
        setToastSuccess('API key deleted successfully!');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error deleting API key:', error);
        setToastError('Failed to delete API key. Please try again.');
      }
    }
  };

  const handleCopyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback((prev) => ({ ...prev, [type]: true }));
      setTimeout(() => {
        setCopyFeedback((prev) => ({ ...prev, [type]: false }));
      }, 2000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to copy text:', err);
      setCopyFeedback((prev) => ({ ...prev, [type]: false }));
    }
  };

  const handleDecimalLayoutChange = (nextLayout) => {
    if (nextLayout === decimalLayout) return;
    dispatch(userActions.upsertUserPreferences(buildDecimalLayoutPatch(nextLayout)));
  };

  const profileName = getProfileDisplayName(user, isCompanyUser);
  const profileInitials = getInitials(user, isCompanyUser);

  const contactSectionRows = useMemo(
    () => buildProfileContactRows(user, isCompanyUser, profileForm, profileEditOpen),
    [user, isCompanyUser, profileForm, profileEditOpen],
  );
  const addressSectionRows = useMemo(
    () => buildProfileAddressRows(user, isCompanyUser, profileForm, profileEditOpen),
    [user, isCompanyUser, profileForm, profileEditOpen],
  );

  const availableOnRampCurrencies = useMemo(
    () => getAvailableOnRampCurrencies(onRampAccounts, supportedOnRampCurrencies),
    [onRampAccounts, supportedOnRampCurrencies],
  );

  const availableOnRampRails = useMemo(
    () => getOnRampRailsForCurrency(onRampAccounts, selectedOnRampCurrency),
    [onRampAccounts, selectedOnRampCurrency],
  );

  useEffect(() => {
    if (availableOnRampCurrencies.length > 0 && !availableOnRampCurrencies.includes(selectedOnRampCurrency)) {
      if (availableOnRampCurrencies.includes('EUR')) {
        setSelectedOnRampCurrency('EUR');
      } else {
        setSelectedOnRampCurrency(availableOnRampCurrencies[0]);
      }
    }
  }, [availableOnRampCurrencies, selectedOnRampCurrency]);

  useEffect(() => {
    if (availableOnRampRails.length === 0) {
      setSelectedOnRampRailType(null);
      return;
    }
    const stillValid = availableOnRampRails.some(
      (rail) => String(rail.type).toLowerCase() === String(selectedOnRampRailType || '').toLowerCase(),
    );
    if (!stillValid) {
      setSelectedOnRampRailType(getPreferredRailType(availableOnRampRails));
    }
  }, [availableOnRampRails, selectedOnRampRailType]);

  const handleOnRampCurrencyChange = (currency) => {
    setSelectedOnRampCurrency(currency);
    setSelectedOnRampRailType(null);
  };

  const activeOnRampAccount = useMemo(() => {
    const account = getOnRampAccountByCurrency(onRampAccounts, selectedOnRampCurrency, selectedOnRampRailType);
    if (account) return account;
    if (bankAccount && String(bankAccount.currency || 'EUR').toUpperCase() === selectedOnRampCurrency) {
      return bankAccount;
    }
    return bankAccount || null;
  }, [onRampAccounts, selectedOnRampCurrency, selectedOnRampRailType, bankAccount]);

  const onRampFields = useMemo(() => getOnRampAccountFields(activeOnRampAccount), [activeOnRampAccount]);
  const paymentMethodLabel = useMemo(
    () => getPaymentMethodLabel(activeOnRampAccount, selectedOnRampCurrency),
    [activeOnRampAccount, selectedOnRampCurrency],
  );

  const profileTypeLabel = isCompanyUser ? 'Business profile' : 'Personal profile';

  return (
    <Layout hideHeader mainPaddingClassName='px-6 py-9 sm:px-11'>
      <div className='mx-auto flex w-full max-w-[1180px] flex-col gap-7'>
        <div className='mb-1 flex items-start justify-between gap-6 pr-[280px]'>
          <div>
            <div className='nav-label mb-2.5'>Account</div>
            <h1 className='m-0 font-display text-[38px] font-normal leading-[1.1] tracking-[-0.02em] text-[var(--n900)]'>
              Profile &amp; preferences
            </h1>
          </div>
        </div>

        <div className='flex max-w-[960px] flex-col gap-[22px]'>
          <section id='account-section-profile' className={`${accountCardClass} flex items-start gap-[22px]`}>
            <div className='flex h-[76px] w-[76px] shrink-0 items-center justify-center rounded-full bg-[var(--n800)] font-display text-[26px] font-medium tracking-[0.02em] text-white'>
              {profileInitials}
            </div>
            <div className='min-w-[200px] flex-1 pt-0.5'>
              <div className='mb-1 text-[11px] font-semibold uppercase tracking-[0.13em] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>
                {profileTypeLabel}
              </div>
              <h2 className='m-0 mb-1 font-display text-[28px] font-normal leading-[1.1] tracking-[-0.015em] text-[var(--n900)]'>
                {isCompanyUser ? getCompanyName(user?.company) || profileName : profileName}
              </h2>
            </div>
            {profileEditOpen ? (
              <div className='flex shrink-0 items-center gap-2 self-start'>
                <OutlineButton type='button' onClick={handleCancelProfileEdit}>
                  Cancel
                </OutlineButton>
                <button
                  type='submit'
                  form='account-profile-form'
                  disabled={updatingUser}
                  className='inline-flex h-[38px] cursor-pointer items-center justify-center rounded-lg bg-[var(--n800)] px-4 text-[12.5px] font-medium text-white hover:opacity-90 disabled:opacity-50 font-[family-name:var(--font-ui)]'
                >
                  Save changes
                </button>
              </div>
            ) : (
              <OutlineButton className='shrink-0 self-start' onClick={() => setProfileEditOpen(true)}>
                <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                  <path d='M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z' />
                </svg>
                Edit profile
              </OutlineButton>
            )}
          </section>

          <form id='account-profile-form' onSubmit={handleProfileSubmit} className='grid grid-cols-1 gap-[22px] md:grid-cols-2'>
            {contactSectionRows.length > 0 && (
              <ProfileInfoSection
                title='Profile information'
                rows={contactSectionRows}
                editing={profileEditOpen}
                onFieldChange={handleProfileChange}
              />
            )}
            {addressSectionRows.length > 0 && (
              <ProfileInfoSection
                title={isCompanyUser ? 'Business address' : 'Address'}
                rows={addressSectionRows}
                editing={profileEditOpen}
                onFieldChange={handleProfileChange}
              />
            )}
          </form>

          <SettingsPanel title='Display' className='md:col-span-2' id='account-section-display'>
            <SecurityActionRow
              title='Number format'
              description={`How amounts are shown across the app. Example: ${formatNumberWithDecimalLayout(1234.56, decimalLayout)}`}
              action={
                <select
                  id='decimal-layout'
                  value={decimalLayout}
                  disabled={upsertingUserPreferences}
                  onChange={(e) => handleDecimalLayoutChange(e.target.value)}
                  className='h-9 min-w-[180px] rounded-lg border border-[var(--color-border)] bg-[var(--white)] px-3 text-[12.5px] font-medium text-[var(--n900)] font-[family-name:var(--font-ui)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--n800)]/20 disabled:opacity-50'
                >
                  <option value={DECIMAL_LAYOUT.GERMAN}>German (1.234,56)</option>
                  <option value={DECIMAL_LAYOUT.ENGLISH}>English (1,234.56)</option>
                </select>
              }
              isLast
            />
          </SettingsPanel>

          <SettingsPanel title='Security' className='md:col-span-2' id='account-section-security'>
            <SecurityActionRow
              title='Password'
              description='Update your account password'
              action={
                <OutlineButton type='button' onClick={() => setPasswordEditOpen((open) => !open)}>
                  Change
                </OutlineButton>
              }
            />
            {passwordEditOpen && (
              <div className='border-b border-[var(--color-border)] px-[22px] py-4'>
                <form onSubmit={handleSecuritySubmit} className='space-y-4'>
                  <input type='text' autoComplete='username' className='hidden' tabIndex={-1} aria-hidden='true' />
                  <div className='space-y-2'>
                    <label htmlFor='currentPassword' className='text-sm font-medium text-[var(--n900)]'>
                      Current password
                    </label>
                    <input
                      id='currentPassword'
                      type='password'
                      value={securityForm.currentPassword}
                      onChange={(e) => handleSecurityChange('currentPassword', e.target.value)}
                      autoComplete='current-password'
                      className={inputClassName}
                    />
                  </div>
                  <div className='space-y-2'>
                    <label htmlFor='newPassword' className='text-sm font-medium text-[var(--n900)]'>
                      New password
                    </label>
                    <input
                      id='newPassword'
                      type='password'
                      value={securityForm.newPassword}
                      onChange={(e) => handleSecurityChange('newPassword', e.target.value)}
                      autoComplete='new-password'
                      className={inputClassName}
                    />
                  </div>
                  <div className='space-y-2'>
                    <label htmlFor='confirmPassword' className='text-sm font-medium text-[var(--n900)]'>
                      Confirm new password
                    </label>
                    <input
                      id='confirmPassword'
                      type='password'
                      value={securityForm.confirmPassword}
                      onChange={(e) => handleSecurityChange('confirmPassword', e.target.value)}
                      autoComplete='new-password'
                      className={inputClassName}
                    />
                  </div>
                  <div className='flex flex-wrap gap-2'>
                    <button
                      type='submit'
                      disabled={
                        changingPassword ||
                        !securityForm.currentPassword ||
                        !securityForm.newPassword ||
                        securityForm.newPassword !== securityForm.confirmPassword
                      }
                      className='inline-flex h-10 items-center justify-center rounded-lg bg-[var(--n800)] px-4 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50'
                    >
                      {changingPassword ? 'Updating…' : 'Change password'}
                    </button>
                    <OutlineButton type='button' onClick={() => setPasswordEditOpen(false)}>
                      Cancel
                    </OutlineButton>
                  </div>
                </form>
              </div>
            )}
            <SecurityActionRow
              title='Two-factor authentication'
              description='Secure your account with two-factor authentication'
              badge={
                twoFactorEnabled ? (
                  <span className='rounded-full bg-[rgba(30,122,68,0.1)] px-[7px] py-0.5 text-[9.5px] font-bold uppercase tracking-[0.08em] text-[#1e7a44]'>
                    Enabled
                  </span>
                ) : null
              }
              action={
                <ToggleSwitch
                  checked={twoFactorEnabled}
                  onChange={() => setTwoFactorEnabled(!twoFactorEnabled)}
                  ariaLabel='Enable two-factor authentication'
                />
              }
              isLast
            />
          </SettingsPanel>

          <SettingsPanel title='Banking' id='account-section-banking'>
            <div className='border-b border-[var(--color-border)] px-[22px] py-4'>
              <div className='flex flex-wrap items-center justify-between gap-3'>
                <p className='m-0 text-sm text-[var(--text-secondary)]'>
                  Transfer funds to your account. Use these deposit details from your bank app or branch.
                </p>
                <div className='flex flex-wrap gap-2'>
                  {availableOnRampCurrencies.length > 1 && (
                    <div className='flex gap-1 rounded-lg border border-[var(--color-border)] bg-[var(--off)] p-1'>
                      {availableOnRampCurrencies.map((curr) => {
                        const isSelected = curr === selectedOnRampCurrency;
                        return (
                          <button
                            key={curr}
                            type='button'
                            onClick={() => handleOnRampCurrencyChange(curr)}
                            className={`rounded px-2.5 py-1 text-xs font-semibold transition-colors ${
                              isSelected
                                ? 'bg-[var(--white)] text-[var(--n900)] shadow-[var(--shadow-sm)]'
                                : 'text-[var(--text-secondary)] hover:text-[var(--n800)]'
                            }`}
                          >
                            {curr}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {availableOnRampRails.length > 1 && (
                    <div className='flex flex-wrap gap-1 rounded-lg border border-[var(--color-border)] bg-[var(--off)] p-1'>
                      {availableOnRampRails.map((rail) => {
                        const isSelected =
                          String(rail.type).toLowerCase() === String(selectedOnRampRailType || '').toLowerCase();
                        return (
                          <button
                            key={rail.key}
                            type='button'
                            onClick={() => setSelectedOnRampRailType(rail.type)}
                            className={`rounded px-2.5 py-1 text-xs font-semibold transition-colors font-[system-ui,-apple-system,BlinkMacSystemFont,sans-serif] ${
                              isSelected
                                ? 'bg-[var(--white)] text-[var(--n900)] shadow-[var(--shadow-sm)]'
                                : 'text-[var(--text-secondary)] hover:text-[var(--n800)]'
                            }`}
                          >
                            {rail.shortLabel}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {onRampFields.length > 0 ? (
              <div className='px-[22px] py-4'>
                <div className='mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--text-tertiary)]'>
                  <CircleCheckIcon className='h-4 w-4 text-[var(--n800)]' />
                  Your deposit details ({selectedOnRampCurrency}
                  {selectedOnRampRailType ? ` · ${selectedOnRampRailType}` : ''})
                </div>
                <div className='grid gap-3'>
                  {onRampFields.map((field) => (
                    <div
                      key={field.label}
                      className='flex items-center justify-between gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--off)] px-4 py-3'
                    >
                      <div className='min-w-0'>
                        <div className='text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)]'>
                          {field.label}
                        </div>
                        <div
                          className={`mt-0.5 truncate text-sm font-medium text-[var(--n900)] ${
                            field.isMono ? 'font-mono' : ''
                          }`}
                        >
                          {field.value}
                        </div>
                      </div>
                      <button
                        type='button'
                        onClick={() => handleCopyToClipboard(field.value, field.label)}
                        className='text-[var(--text-tertiary)] hover:text-[var(--n800)]'
                        aria-label={`Copy ${field.label}`}
                      >
                        {copyFeedback[field.label] ? (
                          <CheckIcon className='h-4 w-4' />
                        ) : (
                          <CopyIcon className='h-4 w-4' />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
                <p className='mt-3 text-[13px] leading-snug text-[var(--text-secondary)]'>
                  Make a {paymentMethodLabel} using these details. Funds are matched automatically to your account.
                </p>
              </div>
            ) : (
              <div className='px-[22px] py-4'>
                <p className='m-0 text-sm text-[var(--text-secondary)]'>
                  Deposit details for {selectedOnRampCurrency} are not available yet. Please try again later.
                </p>
              </div>
            )}
          </SettingsPanel>

          {isCompany && SHOW_ACCOUNT_RAMP_SETTINGS && (
            <AccountRampSettings
              apiKeys={apiKeys}
              apiKeysLoading={apiKeysLoading}
              newApiKeyName={newApiKeyName}
              copyFeedback={copyFeedback}
              webhookForm={webhookForm}
              onApiKeyNameChange={handleApiKeyNameChange}
              onCreateApiKey={handleCreateApiKey}
              onDeleteApiKey={handleDeleteApiKey}
              onCopyToClipboard={handleCopyToClipboard}
              onWebhookFormChange={(value) => setWebhookForm((prev) => ({ ...prev, endpointUrl: value }))}
              onWebhookEventChange={(key, checked) =>
                setWebhookForm((prev) => ({
                  ...prev,
                  events: { ...prev.events, [key]: checked },
                }))
              }
            />
          )}
        </div>

        {updateUserError && (
          <div className='rounded-lg border border-destructive/20 bg-destructive/10 p-4'>
            <p className='text-sm text-destructive-foreground'>{updateUserError.message}</p>
          </div>
        )}

        {apiKeysError && (
          <div className='rounded-lg border border-destructive/20 bg-destructive/10 p-4'>
            <p className='text-sm text-destructive-foreground'>{apiKeysError.message}</p>
          </div>
        )}
      </div>

      <ErrorToast error={toastError} onDismiss={() => setToastError(null)} />
      <SuccessToast message={toastSuccess} onDismiss={() => setToastSuccess(null)} />
    </Layout>
  );
}

export default Account;
